import React, { useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { deprecate } from 'react-is-deprecated';
import { useDevice } from '@atc/react-device';
import BottomSheet from '../BottomSheet/index.js';
import DesktopPopover from './DesktopPopover.js';

/**
 * A responsive container component that displays content with a primary CTA button at the bottom of the sheet. On
 * desktop, it displays content in an elevated popover anchored relative to a trigger element (such as `SelectButton`).
 * On mobile devices, it renders a sliding bottom sheet with a heading and close button that animates up from the bottom
 * of the viewport. A callback is provided for the CTA button, close button, and clicks outside the sheet.
 */

function PopoverSheet(_ref) {
  let {
    borderColor = 'transparent',
    buttonLabel = 'Done',
    children,
    className,
    heading,
    onClick,
    // deprecated
    onHide = () => {},
    containerElement,
    show,
    position,
    hideCTA = true,
    fullWidth = true,
    renderServerSide = false,
    popoverTriggerId = '',
    useBottomSheet = true,
    ...rest
  } = _ref;
  const popoverRef = useRef(null);
  const handleHideClick = useCallback(() => {
    // handle deprecated onClick callback for onHide functionaliuty
    if (typeof onClick === 'function') {
      onClick();
    }
    onHide();
  }, [onClick, onHide]);
  const handleDocumentClick = event => {
    // only trigger close if the sheet has been attached to the DOM (has ref)
    // and the click happened outside of the sheet
    // use event.ComposedPath in case target event is hiding behind Shadow Root (ex UDS)
    // fallback to event.target as well since event.ComposedPath might not available in old browsers
    if (popoverRef.current) {
      const target = event.composedPath()?.[0] ?? event.target;
      const showPopoverCTA = document.getElementById(popoverTriggerId);
      if (!popoverRef.current?.contains(target) && !showPopoverCTA?.contains(target)) {
        handleHideClick();
      }
    }
  };
  useEffect(() => {
    // Listen for clicks outside of the element
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    }; // Cleanup listener on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const device = useDevice();
  const isXs = device?.is?.xs ?? false;
  if (useBottomSheet && isXs) {
    return /*#__PURE__*/React.createElement(BottomSheet, Object.assign({
      ref: popoverRef,
      show: show,
      onHide: handleHideClick,
      buttonLabel: buttonLabel,
      className: className,
      heading: heading,
      hideCTA: hideCTA,
      renderServerSide: renderServerSide
    }, rest), children);
  }
  return /*#__PURE__*/React.createElement(DesktopPopover, Object.assign({
    ref: popoverRef,
    show: show,
    borderColor: borderColor,
    buttonLabel: buttonLabel,
    onHide: handleHideClick,
    className: className,
    containerElement: containerElement,
    position: position,
    hideCTA: hideCTA,
    fullWidth: fullWidth,
    renderServerSide: renderServerSide,
    onBlur: event => {
      const relatedTarget = event.relatedTarget || document.activeElement;
      if (popoverRef.current && !popoverRef.current.contains(relatedTarget)) {
        handleHideClick();
      }
    }
  }, rest), children);
}
export default PopoverSheet;